import { useCallback, useEffect, useState } from 'react';

declare const __webpack_public_path__: string;

const REACT_SCAN_STORAGE_KEY = 'REACT_SCAN_ENABLED';

/**
 * Utilities for managing ReactScan functionality
 */
export const reactScanStorage = {
  /**
   * Gets the current ReactScan enabled state
   * @returns Whether ReactScan is enabled
   */
  isEnabled: () => {
    return localStorage.getItem(REACT_SCAN_STORAGE_KEY) === 'true';
  },

  /**
   * Checks if ReactScan state exists in storage
   * @returns Whether ReactScan state exists in storage
   */
  isInStorage: () => {
    return localStorage.getItem(REACT_SCAN_STORAGE_KEY) !== null;
  },

  /**
   * Sets the ReactScan enabled state
   * @param enabled Whether ReactScan should be enabled
   */
  set: (enabled: boolean) => {
    localStorage.setItem(REACT_SCAN_STORAGE_KEY, enabled.toString());
  },
};

/**
 * Determines if ReactScan should be initialized
 * @returns Whether ReactScan should be initialized
 */
export function shouldInitializeReactScan(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const isReactScanDisabledInURL = urlParams.get('react-scan') === 'false';

  if (isReactScanDisabledInURL) {
    return false;
  }

  const isReactScanEnabledInURL = urlParams.get('react-scan') === 'true';

  if (isReactScanEnabledInURL) {
    return true;
  }

  const isDevelopment = process.env.NODE_ENV === 'development';
  return (
    reactScanStorage.isEnabled() ||
    (!reactScanStorage.isInStorage() && isDevelopment)
  );
}

export function shouldShowReactScanInDevBanner(): boolean {
  const isDevelopment = process.env.NODE_ENV === 'development';
  return isDevelopment || reactScanStorage.isInStorage();
}

/**
 * Initializes ReactScan by appending script to document head
 */
export function initializeReactScan(): void {
  if (shouldInitializeReactScan()) {
    const script = document.createElement('script');
    script.src = `${__webpack_public_path__}vendors/react-scan.js`;

    document.head.appendChild(script);
  }
}

/**
 * Subscribes to ReactScan load event
 * @param callback Callback to execute when ReactScan is loaded
 * @returns Cleanup function
 */
export function isLoadedSubscriber(callback: () => void) {
  const mutationObserver = new MutationObserver(() => {
    if (document.getElementById('react-scan-root')) {
      callback();
      mutationObserver.disconnect();
    }
  });

  if (shouldShowReactScanInDevBanner()) {
    if (document.getElementById('react-scan-root')) {
      callback();
    } else {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  return () => {
    mutationObserver.disconnect();
  };
}

/**
 * Hook for managing ReactScan state
 * @returns Object containing ReactScan state and toggle function
 */
export function useReactScanStorage() {
  const [enabled, setEnabled] = useState<boolean>(
    reactScanStorage.isEnabled() ||
      Boolean(document.getElementById('react-scan-root')),
  );

  const handleToggle = useCallback((value: boolean, { reload = true } = {}) => {
    setEnabled(value);
    reactScanStorage.set(value);

    if (reload) {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (!reactScanStorage.isInStorage()) {
      unsubscribe = isLoadedSubscriber(() => {
        handleToggle(true, { reload: false });
      });
    }

    return () => unsubscribe?.();
  }, [handleToggle]);

  return {
    enabled,
    handleToggle,
  };
}
